import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';
import { X, Loader2 } from 'lucide-react';
import { CANADIAN_PROVINCES } from '../../data/canadianProvinces';

export default function ProfileCompletionModal({ onComplete, onClose }) {
    const { user, setUser } = useAuth();
    const { language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phone: '',
        address: '',
        address2: '',
        postalCode: '',
        city: '',
        province: '',
        country: 'Canada',
        flightSchool: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newFormData = { ...formData, [name]: value };
        if (name === 'province') {
            newFormData.country = value === 'XX' ? '' : 'Canada';
        }
        setFormData(newFormData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/user/complete-profile.php`,
                formData,
                { withCredentials: true }
            );

            if (response.data.success) {
                setUser({ ...user, ...response.data.user, profileCompleted: true });
                onComplete(formData.province);
            } else {
                setError(language === 'fr' ? 'Erreur lors de la mise à jour du profil' : 'Error updating profile');
            }
        } catch (err) {
            console.error('Profile completion error:', err);
            setError(err.response?.data?.error || (language === 'fr' ? 'Une erreur est survenue' : 'An error occurred'));
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition";
    const labelClass = "block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1";

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-lg w-full shadow-2xl border border-gray-200 dark:border-gray-800 max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="p-5 pb-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-white">
                            {language === 'fr' ? 'Complétez votre profil' : 'Complete your profile'}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {language === 'fr'
                                ? 'Requis pour le calcul des taxes et la facturation'
                                : 'Required for tax calculation and billing'
                            }
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition">
                        <X size={18} className="text-gray-400" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-5 overflow-y-auto flex-1 custom-scrollbar space-y-4">
                    {error && (
                        <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
                        </div>
                    )}

                    {/* Prénom & Nom */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>
                                {language === 'fr' ? 'Prénom' : 'First name'} <span className="text-red-500">*</span>
                            </label>
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>
                                {language === 'fr' ? 'Nom' : 'Last name'} <span className="text-red-500">*</span>
                            </label>
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} />
                        </div>
                    </div>

                    {/* Téléphone */}
                    <div>
                        <label className={labelClass}>
                            {language === 'fr' ? 'Téléphone' : 'Phone'} <span className="text-red-500">*</span>
                        </label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass}
                            placeholder="(XXX) XXX-XXXX" />
                    </div>

                    {/* Adresse */}
                    <div>
                        <label className={labelClass}>
                            {language === 'fr' ? 'Adresse' : 'Address'} <span className="text-red-500">*</span>
                        </label>
                        <input type="text" name="address" value={formData.address} onChange={handleChange} required className={inputClass} />
                    </div>

                    {/* Adresse 2 */}
                    <div>
                        <label className={labelClass}>
                            {language === 'fr' ? 'Adresse (suite)' : 'Address line 2'}
                        </label>
                        <input type="text" name="address2" value={formData.address2} onChange={handleChange} className={inputClass}
                            placeholder={language === 'fr' ? 'Appartement, bureau, etc.' : 'Apartment, office, etc.'} />
                    </div>

                    {/* Code postal & Ville */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>
                                {language === 'fr' ? 'Code postal' : 'Postal code'} <span className="text-red-500">*</span>
                            </label>
                            <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} required className={inputClass}
                                placeholder="A1B 2C3" />
                        </div>
                        <div>
                            <label className={labelClass}>
                                {language === 'fr' ? 'Ville' : 'City'} <span className="text-red-500">*</span>
                            </label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} required className={inputClass} />
                        </div>
                    </div>

                    {/* Province */}
                    <div>
                        <label className={labelClass}>
                            Province/Territoire <span className="text-red-500">*</span>
                        </label>
                        <select name="province" value={formData.province} onChange={handleChange} required className={inputClass}>
                            <option value="" className="text-gray-900 bg-white">{language === 'fr' ? 'Sélectionnez une province' : 'Select a province'}</option>
                            {CANADIAN_PROVINCES.map(p => (
                                <option key={p.code} value={p.code} className="text-gray-900 bg-white">
                                    {language === 'fr' ? p.fr : p.en}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Pays */}
                    <div>
                        <label className={labelClass}>
                            {language === 'fr' ? 'Pays' : 'Country'} <span className="text-red-500">*</span>
                        </label>
                        <input type="text" name="country" value={formData.country} onChange={handleChange} required
                            readOnly={formData.province !== 'XX'}
                            className={`${inputClass} ${formData.province !== 'XX' ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`} />
                    </div>

                    {/* École de pilotage */}
                    <div>
                        <label className={labelClass}>
                            {language === 'fr' ? 'École de pilotage (optionnel)' : 'Flight school (optional)'}
                        </label>
                        <input type="text" name="flightSchool" value={formData.flightSchool} onChange={handleChange} className={inputClass}
                            placeholder={language === 'fr' ? 'Nom de votre école' : 'School name'} />
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 rounded-xl font-bold text-white text-sm bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                {language === 'fr' ? 'Enregistrement...' : 'Saving...'}
                            </>
                        ) : (
                            language === 'fr' ? 'Valider et continuer' : 'Save and continue'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
